// Shared export for any list rendered through FlatObjectList's
// {label, value} columns — one function per format, both driven by the
// exact same column definitions the table itself already renders, so the
// export can never drift out of sync with what's on screen.

// A real .xlsx (not a comma-delimited .csv) — Danish/European Excel opens a
// comma-separated CSV as a single column, since it expects semicolons there
// (comma is the decimal separator in that locale). A genuine spreadsheet
// file has real cells/columns regardless of the reader's locale.
export async function exportToExcel(filename, columns, items) {
  const XLSX = await import('xlsx');
  const cols = columns.filter((c) => c.label);
  const rows = [
    cols.map((c) => c.label),
    ...items.map((item) => cols.map((c) => String(c.value(item) ?? ''))),
  ];
  const sheet = XLSX.utils.aoa_to_sheet(rows);
  sheet['!cols'] = cols.map((c) => ({ wch: Math.max(String(c.label).length, 14) }));
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, sheet, 'Objekter');
  // filename param still names the sheet's own title context, but the
  // downloaded file itself always uses one fixed, predictable name rather
  // than a different filename per page/area — easier to find again later.
  XLSX.writeFile(workbook, 'nordcomply-export.xlsx');
}

// Truncates text with an ellipsis so it actually fits maxWidth at the
// document's current font/size, instead of a flat character-count cutoff —
// a fixed 40-char limit overlapped into the next column on any column
// narrower than "40 characters wide", which is most of them once there are
// more than 2-3 columns on an A4 page.
function truncateToWidth(doc, text, maxWidth) {
  if (doc.getTextWidth(text) <= maxWidth) return text;
  let clipped = text;
  while (clipped.length > 1 && doc.getTextWidth(`${clipped}…`) > maxWidth) {
    clipped = clipped.slice(0, -1);
  }
  return `${clipped}…`;
}

export async function exportToPdf(title, columns, items) {
  const { default: jsPDF } = await import('jspdf');
  const cols = columns.filter((c) => c.label);
  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  const marginX = 36;
  const colGap = 10;
  const pageHeight = doc.internal.pageSize.getHeight();
  const pageWidth = doc.internal.pageSize.getWidth();
  // First column (typically the item's name) gets extra room, same ratio
  // as the on-screen table's own Navn column vs. the rest.
  const weights = cols.map((_, i) => (i === 0 ? 1.6 : 1));
  const totalWeight = weights.reduce((sum, w) => sum + w, 0);
  const usableWidth = pageWidth - marginX * 2;
  const colWidths = weights.map((w) => (w / totalWeight) * usableWidth);
  const colX = colWidths.reduce((acc, w, i) => [...acc, i === 0 ? marginX : acc[i - 1] + colWidths[i - 1]], []);
  let y = 50;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.text(title, marginX, y);
  y += 14;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(120);
  doc.text(`${items.length} objekter · ${new Date().toLocaleDateString('da-DK')}`, marginX, y);
  doc.setTextColor(0);
  y += 22;

  function drawHeader() {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    cols.forEach((c, i) => doc.text(truncateToWidth(doc, String(c.label), colWidths[i] - colGap), colX[i], y));
    y += 6;
    doc.setDrawColor(200);
    doc.line(marginX, y, pageWidth - marginX, y);
    y += 14;
    doc.setFont('helvetica', 'normal');
  }

  drawHeader();
  items.forEach((item) => {
    if (y > pageHeight - 40) {
      doc.addPage();
      y = 50;
      drawHeader();
    }
    cols.forEach((c, i) => {
      const text = String(c.value(item) ?? '');
      doc.text(truncateToWidth(doc, text, colWidths[i] - colGap), colX[i], y);
    });
    y += 16;
  });

  // One fixed, predictable download name rather than a different filename
  // per page/area — the title itself still appears as the document's own
  // heading above.
  doc.save('nordcomply-export.pdf');
}
